import React from 'react';
import { Field } from 'redux-form';
import { connect } from 'react-redux';
import { Control, FieldValues, UseControllerProps, useController } from 'react-hook-form';

import { Select, SelectProps } from '@common/components/form/inputs/select';
import type { StoreState } from '../../../../types/store';
import type { Language } from '../../../../types/objects';

type OwnProps = {
  input: {
    value: string | Array<string>;
    onChange: SelectProps['onChange'];
    onFocus?: Function;
    onBlur?: Function;
  },
  meta: {
    visited?: boolean;
    invalid?: boolean;
  },
  width?: number | string;
  error?: boolean | string;
};

type Props = OwnProps & {
  options: Array<Object>;
};

type OptionProps = {
  option: Language;
  isSelected: boolean;
  onSelect: (option: string) => void;
};

/* eslint-disable react/prefer-stateless-function */
const LanguageOptionComponent = ({ option, isSelected, onSelect }: OptionProps) => (
  <div
    key={option.locale}
    className={`Select-option${isSelected ? ' is-selected' : ''} LanguageOption`}
    onClick={!isSelected ? () => onSelect(option.locale) : undefined}
  >
    <img src={option.icon} alt={option.name} />
    <div>
      <h4>{option.name}</h4>
      <small>{option.name_eng}</small>
    </div>
  </div>
);

const LanguageOption = LanguageOptionComponent;

const LanguageSelect = ({
  input: { value, onChange, onFocus, onBlur },
  meta: { visited, invalid },
  options,
  width = 250,
  error,
}: Props) => (
  <div className={`!tw-w-[${width}px]`}>
    <Select
      valueKey="locale"
      labelKey="name"
      className={visited && invalid || error ? 'Form__control--invalid' : undefined}
      options={options}
      value={value}
      onChange={onChange}
      // @ts-expect-error
      OptionComponent={LanguageOption}
      onFocus={onFocus && (() => onFocus())}
      onBlur={onBlur && (() => onBlur())}
      clearable={false}
      searchable
      error={error}
    />
  </div>
);

const mapStateToProps = (state: StoreState, props: Props) => ({
  // @ts-expect-error
  options: props.translations ? state.language.translations : state.language.available,
});

// @ts-expect-error
const ConnectedLanguageInput = connect<Props, OwnProps>(mapStateToProps)(LanguageSelect);

export const LanguageInput = ConnectedLanguageInput;

/**
 * React Hook Form wrapper for LanguageInput
 * By default the form validation mode is set to `mode: 'onSubmit'`.
 * This means the validation will be shown after submitting the form.
 * You probably want to set this option to `all` to have immediate feedback when a field does not pass the added rules.
 * useForm({ mode: 'all' });
 */
export function LanguageInputHookForm<T extends FieldValues>(
  props: {
    control: Control<T>;
    translations?: boolean;
    width?: number | string;
    label?: string;
  } & UseControllerProps<T>
) {
  const { field, fieldState } = useController(props);
  return (
    <ConnectedLanguageInput
      {...{
          input: {
            value: field.value,
            onChange: field.onChange,
            onFocus: field.onBlur,
            onBlur: field.onBlur,
          },
          meta: {
            visited: fieldState.isTouched,
            invalid: !!fieldState.error,
          },
          translations: props.translations || false,
          width: props.width,
          error: fieldState.error?.message || fieldState.error?.message === '',
        } as any}
    />
  );
}

export default (
  props: {
    name: string;
    translations: boolean;
    width?: number | string;
  } & Props
) => (
  <Field
    {...props}
    component={LanguageInput}
  />
);
